import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  AnimatePresence,
  useReducedMotion,
} from "framer-motion";
import {
  Car,
  MapPin,
  CalendarCheck2,
  CreditCard,
  ShieldCheck,
  Phone,
  Sparkles,
  ChevronRight,
  CheckCircle2,
  Menu,
  X,
  Building2,
  Coffee,
  Music4,
  Camera,
  Tent,
  Waves,
  ParkingMeter,
  ArrowUpRight,
  Star,
  Twitter,
  Linkedin,
  Mail,
  ArrowUp,
  PlayCircle,
  KeyRound,
  Gauge,
  Ticket,
  Users,
  Info,
  ChevronDown,
} from "lucide-react";

/**
 * ExToPark — 2025+ Flagship Landing
 * Single-file React component (Tailwind + Framer Motion + Lucide).
 * Big upgrades vs v1:
 *  - Scroll-driven scenes (hero illustration morphs as you scroll)
 *  - Spotlight cursor + magnetic buttons + micro-interactions
 *  - Sticky section TOC with progress + reading-indicator FAB
 *  - Parallax layers, morphing conic gradient, 3D tilt cards
 *  - Better responsive layout, accessible motion (reduce motion support)
 */
const faqs = [
  {
    q: "What exactly does Park Dat do?",
    a: "We maintain a curated directory of parking spots provided by property owners and venues, plus we offer friendly customer support for parkers and hosts and general guidance around parking tickets.",
    icon: Info,
  },
  {
    q: "Do you operate gates or manage on-site hardware?",
    a: "No. We are a listings and support service only. We do not run on-site hardware, attendants, or lane operations.",
    icon: ShieldCheck,
  },
  {
    q: "Can I list my driveway or lot?",
    a: "Yes. If you have a spot to list, contact us and we’ll help you create a clear, accurate listing with availability details and house rules.",
    icon: MapPin,
  },
  {
    q: "Do you handle payments between guests and hosts?",
    a: "No. We don’t process payments. We can share best-practice templates for agreements and point you to popular payment options.",
    icon: CreditCard,
  },
  {
    q: "Can you help with a parking ticket?",
    a: "We’re not legal advisors, but we provide general guidance: how to read your ticket, organize photo evidence, and where to submit disputes.",
    icon: Ticket,
  },
  {
    q: "How fast can my listing go live?",
    a: "Usually within a few days once we confirm your details and photos. Reach out and we’ll get you set up.",
    icon: CalendarCheck2,
  },
  {
    q: "Who should use Park Dat?",
    a: "Homeowners with driveways, small businesses, churches, and venues with extra space — and parkers looking for straightforward options and support.",
    icon: Users,
  },
  {
    q: "Do you cover my city?",
    a: "We’re growing. Send us a message with your location and we’ll share current coverage and nearby listings.",
    icon: MapPin,
  },
];

// -------------------- Primitives -------------------- //
const Button = ({
  children,
  className = "",
  as: Comp = "button",
  href,
  onClick,
  icon: Icon,
  size = "md",
  variant = "primary",
  ...props
}) => {
  const prefersReduced = useReducedMotion();
  const ref = useRef(null);
  const mvx = useMotionValue(0);
  const mvy = useMotionValue(0);
  const tx = useSpring(mvx, { stiffness: 300, damping: 20 });
  const ty = useSpring(mvy, { stiffness: 300, damping: 20 });

  const handleMove = (e) => {
    if (prefersReduced) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - (rect.left + rect.width / 2);
    const y = e.clientY - (rect.top + rect.height / 2);
    mvx.set(x * 0.12);
    mvy.set(y * 0.12);
  };

  const handleLeave = () => {
    mvx.set(0);
    mvy.set(0);
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-5 py-3 text-sm md:text-base",
    lg: "px-6 py-3.5 text-base md:text-lg",
  };

  const variants = {
    primary:
      "bg-emerald-600 text-white hover:bg-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-400",
    secondary:
      "bg-white text-zinc-900 ring-1 ring-zinc-200 hover:bg-white/80 dark:bg-zinc-900 dark:text-white dark:ring-white/10",
    ghost:
      "bg-transparent text-zinc-900 hover:bg-zinc-100 dark:text-white dark:hover:bg-white/5",
  };

  const base =
    "group relative inline-flex items-center gap-2 rounded-2xl font-semibold shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";
  const style = `${base} ${sizes[size]} ${variants[variant]} ${className}`;

  const CompEl = href ? "a" : Comp;

  return (
    <motion.div
      style={{ x: tx, y: ty }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      ref={ref}
      className="inline-block"
    >
      {React.createElement(
        CompEl,
        { className: style, href, onClick, ...props },
        <>
          {Icon && (
            <Icon className="h-5 w-5 transition-transform group-hover:-rotate-6" />
          )}
          <span>{children}</span>
          {!prefersReduced && (
            <motion.span
              layoutId="btn-glow"
              className="pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-[conic-gradient(at_20%_20%,theme(colors.emerald.400/.25),theme(colors.cyan.400/.25),theme(colors.indigo.500/.25),theme(colors.emerald.400/.25))] blur-md opacity-0 group-hover:opacity-100"
            />
          )}
        </>,
      )}
    </motion.div>
  );
};

const Section = ({ id, className = "", children }) => (
  <section
    id={id}
    className={`relative mx-auto w-full max-w-7xl px-6 md:px-8 ${className}`}
  >
    {children}
  </section>
);

// -------------------- Utilities -------------------- //
const useParallax = (distance = 100) => {
  const { scrollYProgress } = useScroll();
  return useTransform(scrollYProgress, [0, 1], [-distance, distance]);
};

const useCounter = (to = 100, duration = 1.2) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      setValue(Math.floor(p * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return value;
};

// -------------------- Decorative Layers -------------------- //
const Spotlight = ({
  size = 520, // base diameter in px (desktop scales up)
  intensity = 0.18, // 0–1 spotlight strength
  vignette = 0.55, // 0–1 vignette strength
} = {}) => {
  const prefersReduced = useReducedMotion();
  const { scrollYProgress } = useScroll();

  // Cursor position (we'll update CSS vars via rAF for perf)
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Fade spotlight slightly as you scroll down
  const scrollFade = useTransform(
    scrollYProgress,
    [0, 0.25, 1],
    [1, 0.85, 0.75],
  );
  const opacity = useSpring(scrollFade, {
    stiffness: 120,
    damping: 20,
    mass: 0.3,
  });

  // Responsive size (larger on bigger screens)
  const responsiveSize = useMemo(() => {
    if (typeof window === "undefined") return size;
    const w = Math.min(window.innerWidth, 1600);
    return Math.max(size, Math.round(w * 0.42));
  }, [size]);

  // Composable background with layered gradients + grain
  const style = useMemo(() => {
    const s = responsiveSize;
    const glow = `radial-gradient(${s}px ${s}px at var(--sx) var(--sy), hsl(160 60% 60% / ${intensity}), transparent 60%)`;
    const hueShift = `radial-gradient(${s * 1.1}px ${
      s * 1.1
    }px at calc(var(--sx) + 12px) calc(var(--sy) + 10px), hsl(200 90% 60% / ${
      intensity * 0.45
    }), transparent 60%)`;
    const hueShift2 = `radial-gradient(${s * 0.9}px ${
      s * 0.9
    }px at calc(var(--sx) - 16px) calc(var(--sy) - 14px), hsl(260 95% 65% / ${
      intensity * 0.35
    }), transparent 60%)`;
    const vign = `radial-gradient(120% 120% at 50% 40%, transparent ${Math.round(
      (1 - vignette) * 70,
    )}%, rgba(0,0,0,0.25))`;

    // subtle grain texture
    const grain =
      "url('data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'>
           <filter id='n'><feTurbulence baseFrequency='.9' numOctaves='2' seed='2'/><feColorMatrix type='saturate' values='0'/><feComponentTransfer><feFuncA type='table' tableValues='0 0 0 .04 .065 .04 0 0 0'/></feComponentTransfer></filter>
           <rect width='100%' height='100%' filter='url(#n)'/>
         </svg>`,
      ) +
      "')";

    return {
      // Layer order matters: grain on top with soft-light; color glows beneath; vignette last
      backgroundImage: `${grain}, ${glow}, ${hueShift}, ${hueShift2}, ${vign}`,
      backgroundBlendMode: "soft-light, normal, normal, normal, multiply",
      willChange: "transform, opacity, background-position",
    };
  }, [responsiveSize, intensity, vignette]);

  // rAF throttle for pointer updates to avoid layout trashing
  const rafRef = useRef(0);
  const handleMove = (clientX, clientY, el) => {
    if (!el) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      // set MotionValues for potential consumers
      x.set(clientX);
      y.set(clientY);
      el.style.setProperty("--sx", `${clientX}px`);
      el.style.setProperty("--sy", `${clientY}px`);
    });
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10"
      style={{ opacity }}
      onMouseMove={(e) =>
        !prefersReduced && handleMove(e.clientX, e.clientY, e.currentTarget)
      }
      onTouchMove={(e) => {
        if (prefersReduced) return;
        const t = e.touches?.[0];
        if (t) handleMove(t.clientX, t.clientY, e.currentTarget);
      }}
    >
      {/* main canvas */}
      <div className="absolute inset-0" style={style} />

      {/* optional soft color wash to unify backgrounds */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(16,185,129,0.08), rgba(6,182,212,0.06) 40%, rgba(99,102,241,0.06))",
          mixBlendMode: "overlay",
          opacity: 0.6,
        }}
      />

      {/* dark-mode tuning: lighten a touch to keep contrast */}
      <div
        className="absolute inset-0 dark:opacity-70"
        style={{
          background:
            "radial-gradient(120% 120% at 50% 0%, rgba(255,255,255,0.04), transparent 60%)",
          mixBlendMode: "screen",
        }}
      />
    </motion.div>
  );
};

const AnimatedBlobs = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
  >
    <div className="absolute -top-24 -left-24 h-[36rem] w-[36rem] rounded-full bg-[conic-gradient(from_180deg_at_50%_50%,theme(colors.emerald.400/.3),theme(colors.cyan.400/.3),theme(colors.indigo.500/.3),theme(colors.emerald.400/.3))] blur-3xl animate-spin-slow" />
    <div className="absolute top-1/3 -right-20 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle_at_30%_30%,theme(colors.fuchsia.500/.25),transparent_60%)] blur-3xl" />
    <div className="absolute bottom-[-6rem] left-1/4 h-[24rem] w-[24rem] rounded-full bg-[radial-gradient(circle_at_70%_70%,theme(colors.amber.400/.18),transparent_60%)] blur-3xl" />
  </div>
);

const GridOverlay = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 -z-10 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"
  >
    <svg
      className="h-full w-full opacity-[0.06]"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
          <path
            d="M 32 0 L 0 0 0 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="0.5"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  </div>
);

// -------------------- Components -------------------- //
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.2,
  });

  const links = [
    { href: "#services", label: "Services" },
    { href: "#events", label: "Events" },
    { href: "#work", label: "Case Studies" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <div className="sticky top-0 z-50">
      <motion.div
        style={{ scaleX }}
        className="origin-left h-1 w-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-indigo-500"
      />
      <nav className="backdrop-blur supports-[backdrop-filter]:bg-white/50 bg-white/70 dark:bg-zinc-950/70">
        <Section className="flex items-center justify-between py-4">
          <a href="#home" className="flex items-center gap-2">
            <div className="relative">
              <motion.span
                initial={{ rotate: -10 }}
                animate={{ rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 12 }}
                className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-emerald-500 via-cyan-500 to-indigo-600 text-white shadow-lg"
              >
                <ParkingMeter className="h-5 w-5" />
              </motion.span>
              <span className="absolute -bottom-1 -right-1 inline-flex h-3 w-3 animate-ping rounded-full bg-emerald-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium leading-3 text-zinc-800 dark:text-zinc-100">
                Park Dat
              </p>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                Smart Event Parking
              </p>
            </div>
          </a>

          <div className="hidden items-center gap-6 md:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium text-zinc-700 transition hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>

          <button
            className="md:hidden"
            onClick={() => setOpen((s) => !s)}
            aria-label="Open menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </Section>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden"
            >
              <Section className="flex flex-col gap-3 pb-6">
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="rounded-xl bg-white/60 px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-white/80 dark:bg-zinc-900/60 dark:text-zinc-300 dark:hover:bg-zinc-900/80"
                  >
                    {l.label}
                  </a>
                ))}
              </Section>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
};

const Hero = () => {
  const prefersReduced = useReducedMotion();

  // Subtle parallax
  const yEyebrow = useParallax(30);
  const yTitle = useParallax(50);
  const ySub = useParallax(70);
  const yViz = useParallax(40);

  return (
    <header
      id="home"
      className="
        relative overflow-hidden
        bg-[radial-gradient(1400px_800px_at_10%_-10%,theme(colors.emerald.500/12),transparent),
            radial-gradient(1000px_700px_at_110%_0%,theme(colors.indigo.500/12),transparent)]
        dark:bg-[radial-gradient(1400px_800px_at_10%_-10%,theme(colors.emerald.400/12),transparent),
            radial-gradient(1000px_700px_at_110%_0%,theme(colors.indigo.400/12),transparent)]
      "
    >
      <AnimatedBlobs />
      <GridOverlay />

      {/* Top light sweep */}
      <div className="pointer-events-none absolute inset-x-0 -top-28 h-56 opacity-70 [mask-image:linear-gradient(to_bottom,black,transparent)]">
        <div className="mx-auto h-full w-[1200px] max-w-[92vw] rounded-full bg-gradient-to-r from-emerald-400/35 via-cyan-400/35 to-indigo-500/35 blur-3xl" />
      </div>

      <Section className="relative grid w-full grid-cols-1 items-center gap-12 py-16 sm:py-20 md:grid-cols-2 md:gap-12 lg:py-24">
        {/* COPY — first on mobile, left on desktop */}
        <div className="order-1">
          {/* Eyebrow */}

          {/* Headline */}
          <motion.h1
            style={{ y: yTitle }}
            className="
              mt-5 text-balance
              font-extrabold tracking-tight
              text-zinc-900 dark:text-white
              text-[clamp(2.25rem,8.5vw,4.75rem)]
              leading-[1.05]
            "
          >
            Park thousands in{" "}
            <span className="whitespace-nowrap">minutes.</span>{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-emerald-400 via-cyan-500 to-indigo-600 bg-clip-text text-transparent">
                No gridlock.
              </span>
              {!prefersReduced && (
                <motion.span
                  aria-hidden
                  className="absolute -bottom-3 left-0 h-[5px] w-full rounded-full 
                             bg-gradient-to-r from-emerald-400/70 via-cyan-400/70 to-indigo-500/70
                             blur-[2px] opacity-90"
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 140,
                    damping: 20,
                    delay: 0.25,
                  }}
                  style={{ originX: 0 }}
                />
              )}
            </span>
          </motion.h1>

          {/* Subhead */}
          <motion.p
            style={{ y: ySub }}
            className="
              mt-10 max-w-2xl
              text-[clamp(0.98rem,1.6vw,1.15rem)] leading-relaxed
              text-zinc-600 dark:text-zinc-300
            "
          >
            Park Dat lists spots, supports drivers, and guides tickets so fans
            park fast — not circle around.
          </motion.p>

          {/* CTAs */}
          <motion.div
            style={{ y: ySub }}
            className="mt-6 flex flex-wrap items-center gap-3"
          >
            <Button
              href="#contact"
              size="lg"
              variant="primary"
              icon={PlayCircle}
            >
              Get Started
            </Button>
            <Button
              href="#work"
              size="lg"
              variant="secondary"
              className="ring-1 ring-zinc-200/70 dark:ring-white/10"
            >
              See Case Studies
            </Button>
          </motion.div>

          {/* Social proof */}
          <motion.div
            style={{ y: ySub }}
            className="mt-6 grid max-w-lg grid-cols-1 gap-2 text-[13px] text-zinc-500 sm:grid-cols-3"
          >
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              Trusted by top venues
            </div>
            <div className="inline-flex items-center gap-2">
              <Gauge className="h-4 w-4 text-cyan-500" />
              99.9% uptime
            </div>
            <div className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-indigo-500" />
              120+ cities
            </div>
          </motion.div>
        </div>

        {/* VISUAL — second on mobile, right on desktop */}
        <motion.div style={{ y: yViz }} className="order-2">
          <div
            className="
              relative mx-auto aspect-[16/10] w-full max-w-[700px]
              overflow-hidden rounded-[28px]
              border border-white/10 bg-white/60 p-5 backdrop-blur-xl
              shadow-[0_30px_120px_-30px_rgba(0,0,0,.45)]
              dark:bg-white/5
            "
          >
            {/* Interior glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 opacity-70
                         [mask-image:radial-gradient(65%_75%_at_55%_45%,black,transparent)]
                         bg-[conic-gradient(from_210deg_at_60%_40%,theme(colors.emerald.400/30),theme(colors.cyan.400/30),theme(colors.indigo.500/30),theme(colors.emerald.400/30))]"
            />

            {/* Grid */}
            <div className="relative h-full w-full rounded-2xl border border-white/10 bg-gradient-to-b from-white/40 to-white/20 p-4 dark:from-white/5 dark:to-transparent">
              <div className="absolute inset-0 rounded-2xl [background-image:linear-gradient(90deg,rgba(255,255,255,.06)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.06)_1px,transparent_1px)]; [background-size:28px_28px,28px_28px]" />

              {/* Floating chips */}
              <div className="absolute left-4 top-4 flex gap-2">
                <div className="rounded-xl bg-black/55 px-3 py-1 text-[11px] text-white ring-1 ring-white/10 dark:bg-white/10">
                  ETA: 7m → 4m
                </div>
                <div className="rounded-xl bg-black/55 px-3 py-1 text-[11px] text-emerald-200 ring-1 ring-white/10 dark:bg-white/10">
                  Surge ↓
                </div>
              </div>

              {/* Feature rail */}
              <div className="absolute right-3 top-3 space-y-2">
                {[
                  { icon: Gauge, label: "Ingress" },
                  { icon: KeyRound, label: "Passes" },
                  { icon: MapPin, label: "Routing" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 rounded-xl bg-white/40 px-2 py-1 text-[11px] text-zinc-700 ring-1 ring-white/40 backdrop-blur dark:bg-white/10 dark:text-zinc-200 dark:ring-white/10"
                  >
                    <Icon className="h-3.5 w-3.5 text-zinc-800 dark:text-white/80" />
                    {label}
                  </div>
                ))}
              </div>

              {/* Heat spots (gentle) */}
              {!prefersReduced && (
                <motion.div
                  className="absolute inset-0"
                  animate={{
                    opacity: [0.25, 0.55, 0.25],
                    x: ["-6%", "6%", "-6%"],
                  }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    background:
                      "radial-gradient(240px 140px at 35% 35%, rgba(16,185,129,0.22), transparent 60%), radial-gradient(220px 160px at 75% 65%, rgba(99,102,241,0.22), transparent 60%)",
                  }}
                />
              )}

              {/* Lane curve + cars */}
              <div className="absolute inset-x-6 bottom-7 h-24">
                <svg
                  className="h-full w-full"
                  viewBox="0 0 560 100"
                  fill="none"
                >
                  <path
                    d="M10 65 C 120 10, 220 10, 330 55 S 480 90, 550 30"
                    stroke="currentColor"
                    className="text-zinc-700/30 dark:text-white/30"
                    strokeWidth="2"
                    strokeDasharray="6 10"
                  />
                </svg>
                {!prefersReduced && (
                  <motion.div
                    className="absolute left-2 top-5 flex gap-2"
                    animate={{ x: [0, 520] }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    {[...Array(6)].map((_, i) => (
                      <Car
                        key={i}
                        className="h-4 w-4 text-zinc-600/70 dark:text-zinc-300/80"
                      />
                    ))}
                  </motion.div>
                )}
              </div>

              {/* Left rail accent */}
              <div className="absolute left-3 top-0 h-full w-1 rounded bg-white/15" />
            </div>
          </div>
        </motion.div>
      </Section>
    </header>
  );
};

const useHashOpen = (target = "#contact-panel") => {
  const [open, setOpen] = useState(
    typeof window !== "undefined" && window.location.hash === target,
  );
  useEffect(() => {
    const onHash = () => setOpen(window.location.hash === target);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [target]);
  return [open, setOpen];
};

const phoneRegExp = /^[+()\-\s.\d]{7,}$/;

const Schema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  phone: Yup.string()
    .matches(phoneRegExp, "Enter a valid phone number")
    .required("Phone is required"),
  topic: Yup.string()
    .oneOf(["listing", "need", "ticket", "partnership", "other"])
    .required("Topic is required"),
  subject: Yup.string().trim().required("Subject is required"),
  message: Yup.string().trim().required("Message is required"),
  smsConsent: Yup.boolean().oneOf(
    [true],
    "Please consent to receive SMS for your inquiry.",
  ),
  // honey is optional (spam trap)
});

const ContactOverlay = () => {
  const [open, setOpen] = useHashOpen("#contact-panel");
  const prefersReduced = useReducedMotion();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const close = () => {
    if (window.location.hash === "#contact-panel") {
      window.history.back();
      return;
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {/* dim background */}
      <motion.div
        key="contact-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm"
        onClick={close}
      />

      {/* centered panel */}
      <motion.div
        key="contact-panel"
        initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 40 }}
        animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
        exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 40 }}
        transition={{ type: "spring", stiffness: 140, damping: 20 }}
        className="fixed inset-0 z-[80] grid place-items-center px-2 py-6 sm:px-4 sm:py-10"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contactTitle"
      >
        <div className="relative w-full max-w-[min(100vw-1rem,48rem)] overflow-hidden rounded-3xl p-[1px]">
          {/* conic border */}
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-80"
            style={{
              background:
                "conic-gradient(from 180deg at 50% 50%, rgba(16,185,129,.35), rgba(6,182,212,.35), rgba(79,70,229,.35), rgba(16,185,129,.35))",
            }}
          />

          <div className="relative max-h-[90vh] overflow-y-auto rounded-3xl bg-white/95 text-zinc-900 backdrop-blur dark:bg-zinc-900/95 dark:text-white">
            {/* sticky header */}
            <div className="sticky top-0 z-10 border-b border-zinc-200/60 bg-white/80 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-zinc-900/80 sm:px-6">
              <div className="flex items-start justify-between">
                <div className="pr-2">
                  <h2
                    id="contactTitle"
                    className="text-xl font-bold tracking-tight sm:text-2xl"
                  >
                    Tell us how we can help
                  </h2>
                  <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300 sm:text-sm">
                    Listings, customer support, or parking ticket guidance —
                    we’ll reply within 24 hours.
                  </p>
                </div>
                <button
                  onClick={close}
                  aria-label="Close contact form"
                  className="-mr-1 rounded-xl p-2 text-zinc-500 hover:bg-white/50 hover:text-zinc-800 dark:hover:bg-white/10"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>

            <div className="px-4 py-5 sm:px-6 sm:py-6">
              {!sent ? (
                <Formik
                  initialValues={{
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    topic: "listing",
                    subject: "",
                    message: "",
                    smsConsent: false,
                    honey: "",
                  }}
                  validationSchema={Schema}
                  onSubmit={async (values, { setSubmitting }) => {
                    if (values.honey.trim()) {
                      setSent(true);
                      setSubmitting(false);
                      return;
                    }

                    setSending(true);
                    try {
                      const res = await fetch(
                        "https://parkdatbackend.vercel.app/api/contactus",
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(values),
                        },
                      );

                      if (res.ok) {
                        setSent(true);
                      } else {
                        alert("Error sending message. Please try again later.");
                      }
                    } catch (err) {
                      console.error(err);
                      alert("Network error.");
                    }
                    setSending(false);
                    setSubmitting(false);
                  }}
                  validateOnBlur
                  validateOnChange
                >
                  {({ errors, touched, isSubmitting }) => (
                    <Form className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
                      {/* honeypot */}
                      <Field
                        type="text"
                        name="honey"
                        className="hidden"
                        tabIndex={-1}
                        autoComplete="off"
                      />

                      {/* First Name */}
                      <div className="md:col-span-1">
                        <label className="mb-1 block text-sm font-medium">
                          First name
                        </label>
                        <Field
                          name="firstName"
                          placeholder="Your first name"
                          className={`w-full rounded-xl border px-3 py-3 text-sm sm:text-base outline-none ring-emerald-400/30 focus:ring-2 dark:bg-zinc-900
                            ${
                              errors.firstName && touched.firstName
                                ? "border-red-500 focus:ring-red-400 dark:border-red-500"
                                : "border-zinc-200 dark:border-white/10 bg-white"
                            }`}
                        />
                        <ErrorMessage
                          name="firstName"
                          component="p"
                          className="mt-1 text-xs text-red-600"
                        />
                      </div>

                      {/* Last Name */}
                      <div className="md:col-span-1">
                        <label className="mb-1 block text-sm font-medium">
                          Last name
                        </label>
                        <Field
                          name="lastName"
                          placeholder="Your last name"
                          className={`w-full rounded-xl border px-3 py-3 text-sm sm:text-base outline-none ring-emerald-400/30 focus:ring-2 dark:bg-zinc-900
                            ${
                              errors.lastName && touched.lastName
                                ? "border-red-500 focus:ring-red-400 dark:border-red-500"
                                : "border-zinc-200 dark:border-white/10 bg-white"
                            }`}
                        />
                        <ErrorMessage
                          name="lastName"
                          component="p"
                          className="mt-1 text-xs text-red-600"
                        />
                      </div>

                      {/* Email */}
                      <div className="md:col-span-1">
                        <label className="mb-1 block text-sm font-medium">
                          Email
                        </label>
                        <Field
                          name="email"
                          type="email"
                          inputMode="email"
                          placeholder="you@email.com"
                          className={`w-full rounded-xl border px-3 py-3 text-sm sm:text-base outline-none ring-emerald-400/30 focus:ring-2 dark:bg-zinc-900
                            ${
                              errors.email && touched.email
                                ? "border-red-500 focus:ring-red-400 dark:border-red-500"
                                : "border-zinc-200 dark:border-white/10 bg-white"
                            }`}
                        />
                        <ErrorMessage
                          name="email"
                          component="p"
                          className="mt-1 text-xs text-red-600"
                        />
                      </div>

                      {/* Phone (required) */}
                      <div className="md:col-span-1">
                        <label className="mb-1 block text-sm font-medium">
                          Phone
                        </label>
                        <Field
                          name="phone"
                          inputMode="tel"
                          placeholder="(555) 123-4567"
                          className={`w-full rounded-xl border px-3 py-3 text-sm sm:text-base outline-none ring-emerald-400/30 focus:ring-2 dark:bg-zinc-900
                            ${
                              errors.phone && touched.phone
                                ? "border-red-500 focus:ring-red-400 dark:border-red-500"
                                : "border-zinc-200 dark:border-white/10 bg-white"
                            }`}
                        />
                        <ErrorMessage
                          name="phone"
                          component="p"
                          className="mt-1 text-xs text-red-600"
                        />
                      </div>

                      {/* Topic */}
                      <div className="md:col-span-1">
                        <label className="mb-1 block text-sm font-medium">
                          Topic
                        </label>
                        <div className="relative group">
                          <Field
                            as="select"
                            name="topic"
                            className={`w-full appearance-none rounded-xl border px-3 pr-9 py-3 text-sm sm:text-base outline-none ring-emerald-400/30 backdrop-blur transition-colors
                              dark:bg-zinc-900/70 dark:text-white dark:hover:border-white/20 dark:focus:border-emerald-400/40
                              ${
                                errors.topic && touched.topic
                                  ? "border-red-500 focus:ring-red-400 dark:border-red-500"
                                  : "border-zinc-200/60 bg-white/80 text-zinc-900 hover:border-zinc-300/80 focus:border-emerald-300/60 dark:border-white/10"
                              }`}
                          >
                            <option value="listing">Listing a spot</option>
                            <option value="need">Need parking</option>
                            <option value="ticket">
                              Parking ticket guidance
                            </option>
                            <option value="partnership">
                              Partnership / Venue
                            </option>
                            <option value="other">Other</option>
                          </Field>
                          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500 transition-colors group-focus-within:text-emerald-500 dark:text-zinc-400" />
                        </div>
                        <ErrorMessage
                          name="topic"
                          component="p"
                          className="mt-1 text-xs text-red-600"
                        />
                      </div>

                      {/* Subject (required) */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium">
                          Subject
                        </label>
                        <Field
                          name="subject"
                          placeholder="Short subject"
                          className={`w-full rounded-xl border px-3 py-3 text-sm sm:text-base outline-none ring-emerald-400/30 focus:ring-2 dark:bg-zinc-900
                            ${
                              errors.subject && touched.subject
                                ? "border-red-500 focus:ring-red-400 dark:border-red-500"
                                : "border-zinc-200 dark:border-white/10 bg-white"
                            }`}
                        />
                        <ErrorMessage
                          name="subject"
                          component="p"
                          className="mt-1 text-xs text-red-600"
                        />
                      </div>

                      {/* Message */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium">
                          Message
                        </label>
                        <Field
                          as="textarea"
                          name="message"
                          rows={6}
                          placeholder="Tell us about your listing, question, or ticket issue…"
                          className={`w-full resize-y rounded-xl border px-3 py-3 text-sm sm:text-base outline-none ring-emerald-400/30 focus:ring-2 dark:bg-zinc-900
                            ${
                              errors.message && touched.message
                                ? "border-red-500 focus:ring-red-400 dark:border-red-500"
                                : "border-zinc-200 dark:border-white/10 bg-white"
                            }`}
                        />
                        <ErrorMessage
                          name="message"
                          component="p"
                          className="mt-1 text-xs text-red-600"
                        />
                      </div>

                      {/* SMS consent at the end (required) */}
                      <div
                        id="sms-consent"
                        className="md:col-span-2 -mt-1 rounded-xl bg-zinc-50/80 p-3 ring-1 ring-zinc-200/70 dark:bg-white/5 dark:ring-white/10"
                      >
                        <label className="flex items-start gap-3">
                          <Field
                            type="checkbox"
                            name="smsConsent"
                            className={`mt-1 h-4 w-4 shrink-0 rounded focus:ring-emerald-500 dark:bg-zinc-900
                              ${
                                errors.smsConsent && touched.smsConsent
                                  ? "border-red-500 text-red-600"
                                  : "border-zinc-300 text-emerald-600 dark:border-white/20"
                              }`}
                          />
                          <span className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-300">
                            I consent to receive SMS/MMS from Park Dat at the
                            number provided. Message &amp; data rates may apply.
                            Frequency varies. Reply <b>STOP</b> to opt out,{" "}
                            <b>HELP</b> for help. You can also reach us at{" "}
                            <b>+1 (888) 502-5242</b>. See our{" "}
                            <a
                              href="/privacy"
                              className="font-medium underline decoration-emerald-400 underline-offset-2 hover:text-emerald-600"
                            >
                              Privacy Policy
                            </a>
                            .
                          </span>
                        </label>
                        <ErrorMessage
                          name="smsConsent"
                          component="p"
                          className="mt-2 text-xs text-red-600"
                        />
                      </div>

                      {/* actions */}
                      <div className="md:col-span-2 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-zinc-500">
                          By sending, you agree we can contact you about your
                          inquiry.
                        </p>
                        <Button
                          as="button"
                          type="submit"
                          size="lg"
                          className="bg-emerald-600 text-white hover:bg-emerald-500"
                          icon={Mail}
                          disabled={isSubmitting || sending}
                        >
                          {sending ? "Sending…" : "Send message"}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              ) : (
                <div className="grid place-items-center py-14 text-center sm:py-16">
                  <div className="mb-3 inline-flex items-center justify-center rounded-xl bg-emerald-500/10 p-3 ring-1 ring-emerald-400/20">
                    <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-semibold sm:text-xl">
                    Message sent successfully 🎉
                  </h3>
                  <p className="mt-1 max-w-md text-sm text-zinc-600 dark:text-zinc-300">
                    Thank you for reaching out! Our customer support team will
                    reply within <b>24 hours</b>. For urgent matters, you can
                    also email us directly at{" "}
                    <a
                      className="underline decoration-emerald-400 hover:text-emerald-600"
                      href="mailto:sales@Parkdat.com"
                    >
                      sales@parkdat.com
                    </a>
                    .
                  </p>
                  <div className="mt-6">
                    <Button onClick={close} variant="secondary">
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* mobile safe-area spacing */}
            <div className="h-[env(safe-area-inset-bottom,0px)]" />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const Clients = () => {
  const brands = [
    "StapleSquare",
    "SunField Arena",
    "MetroCity",
    "LiveNation+",
    "Skyline Bowl",
    "CivicStage",
    "Pier 45",
    "United Dome",
    "Fenway Park Boston",
    "Yankee Stadium New York",
    "Wrigley Field Chicago",
    "Dodger Stadium Los Angeles",
    "Madison Square Garden New York",
    "Barclays Center Brooklyn",
    "AT&T Stadium Dallas",
    "Chase Center San Francisco",
  ];

  const Row = () => (
    <div className="flex shrink-0 items-center gap-6 pr-6">
      {brands.map((b) => (
        <div
          key={b}
          className="grid h-10 min-w-[160px] place-items-center rounded-xl border border-zinc-200/60 bg-white/60 px-4 text-sm font-medium text-zinc-700 dark:border-white/10 dark:bg-zinc-900/60 dark:text-zinc-200"
        >
          {b}
        </div>
      ))}
    </div>
  );
  return (
    <Section className="py-14">
      <p className="mb-6 text-center text-xs uppercase tracking-[0.2em] text-zinc-500">
        Trusted by venues & cities
      </p>
      <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
        <motion.div
          initial={{ x: 0 }}
          animate={{ x: [0, "-50%"] }}
          transition={{ repeat: Infinity, duration: 28, ease: "linear" }}
          className="flex w-[200%]"
        >
          <Row />
          <Row />
        </motion.div>
      </div>
    </Section>
  );
};
const Feature = ({ icon: Icon, title, desc }) => (
  <motion.div
    whileInView={{ opacity: 1, y: 0 }}
    initial={{ opacity: 0, y: 24 }}
    viewport={{ once: true }}
    className="group rounded-2xl border border-zinc-200/60 bg-white/70 p-6 shadow-sm transition hover:shadow-xl dark:border-white/10 dark:bg-zinc-900/60"
  >
    <div className="mb-3 inline-flex rounded-xl bg-emerald-50 p-2 text-emerald-600 ring-1 ring-emerald-100 group-hover:scale-105 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-400/20">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="mb-1 text-lg font-semibold text-zinc-900 dark:text-white">
      {title}
    </h3>
    <p className="text-sm text-zinc-600 dark:text-zinc-300">{desc}</p>
  </motion.div>
);

const Services = () => (
  <Section id="services" className="py-20">
    <div className="mb-10 text-center">
      <h2 className="text-3xl font-semibold md:text-4xl">
        Parking services built for every need
      </h2>
      <p className="mt-2 text-zinc-600 dark:text-zinc-300">
        From driveways to stadiums — we help drivers and hosts connect.
      </p>
    </div>
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <Feature
        icon={MapPin}
        title="Parking Spot Listings"
        desc="List your driveway, lot, or venue space so drivers can find it fast."
      />
      <Feature
        icon={Phone}
        title="Customer Support"
        desc="Dedicated help for drivers and hosts — whether it’s directions, access, or questions."
      />
      <Feature
        icon={ShieldCheck}
        title="Parking Ticket Guidance"
        desc="Clear steps and resources to understand, contest, or resolve tickets."
      />
      <Feature
        icon={Building2}
        title="Venue & Business Support"
        desc="Tailored listing support for arenas, small businesses, and community events."
      />
      <Feature
        icon={CalendarCheck2}
        title="Event Coordination"
        desc="Plan ahead for concerts, games, and gatherings with spot availability and support."
      />
      <Feature
        icon={CreditCard}
        title="Easy Access"
        desc="Simple processes so drivers park quickly — without the stress."
      />
    </div>
  </Section>
);

const Events = () => {
  const items = [
    {
      icon: Music4,
      label: "Concerts & Shows",
      points: ["Clear directions", "After‑hours access", "Host contact"],
    },
    {
      icon: Camera,
      label: "Film & Festivals",
      points: ["Temporary listings", "Crew & guest notes", "Nearby options"],
    },
    {
      icon: Tent,
      label: "Sports & Tailgates",
      points: ["Event‑day info", "House rules", "Overflow ideas"],
    },
    {
      icon: Coffee,
      label: "Small Businesses",
      points: ["Customer parking", "Weekend availability", "Simple guidance"],
    },
    {
      icon: Waves,
      label: "Seasonal & Beaches",
      points: ["Peak‑season tips", "Local restrictions", "Updated hours"],
    },
  ];
  return (
    <Section id="events" className="py-16">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h2 className="text-3xl font-semibold md:text-4xl">
            Where our listings help
          </h2>
          <p className="mt-2 text-zinc-600 dark:text-zinc-300">
            From home driveways to arena days — simple, supportive parking info.
          </p>
        </div>
        <div className="hidden md:block text-sm text-zinc-500">Swipe ▶︎</div>
      </div>
      <div className="relative">
        <motion.div
          initial={{ x: 0 }}
          whileHover={{ x: -20 }}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map(({ icon: Icon, label, points }) => (
            <motion.div
              key={label}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              viewport={{ once: true }}
              className="snap-center min-w-[260px] p-[1px] rounded-2xl bg-gradient-to-tr from-emerald-400/25 via-cyan-400/25 to-indigo-500/25"
            >
              <div className="rounded-2xl border border-white/20 bg-white/70 p-6 shadow-sm dark:border-white/10 dark:bg-zinc-900/60">
                <div className="mb-3 inline-flex items-center gap-2">
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/20">
                    <Icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </span>
                  <h3 className="text-lg font-semibold">{label}</h3>
                </div>
                <ul className="mt-2 space-y-2 text-sm text-zinc-600 dark:text-zinc-300">
                  {points.map((p) => (
                    <li key={p} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

const TiltCard = ({ children }) => {
  const prefersReduced = useReducedMotion();
  const ref = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [state, setState] = useState({ rx: 0, ry: 0, gx: 50, gy: 50 });

  const calc = (clientX, clientY) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return { rx: 0, ry: 0, gx: 50, gy: 50 };
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const px = x / rect.width - 0.5;
    const py = y / rect.height - 0.5;
    const maxTilt = 10;
    const rx = prefersReduced ? 0 : -py * maxTilt;
    const ry = prefersReduced ? 0 : px * maxTilt;
    const gx = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const gy = Math.max(0, Math.min(100, (y / rect.height) * 100));
    return { rx, ry, gx, gy };
  };

  const handleMove = (e) => {
    const p = "touches" in e ? e.touches[0] : e;
    setState(calc(p.clientX, p.clientY));
  };

  const reset = () => setState({ rx: 0, ry: 0, gx: 50, gy: 50 });

  return (
    <div className="[perspective:1200px]">
      <motion.div
        ref={ref}
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          reset();
        }}
        onTouchEnd={() => {
          setHovered(false);
          reset();
        }}
        className="relative rounded-2xl group"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Glow border */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 rounded-2xl p-[1px]"
          style={{
            background:
              "conic-gradient(from 220deg at 50% 50%, rgba(16,185,129,.25), rgba(6,182,212,.25), rgba(99,102,241,.25), rgba(16,185,129,.25))",
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }}
        />

        {/* Card body */}
        <motion.div
          className="relative rounded-2xl border border-zinc-200/60 bg-white/70 p-6 shadow-xl dark:border-white/10 dark:bg-zinc-900/60"
          animate={{
            rotateX: state.rx,
            rotateY: state.ry,
            scale: hovered ? 1.03 : 1,
          }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 18,
            mass: 0.6,
          }}
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* Extra glow on hover */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-pink-500/10 via-purple-500/10 to-blue-500/10 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100" />

          {/* Shine effect */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-2xl"
            style={{
              background: `radial-gradient(400px 240px at ${state.gx}% ${state.gy}%, rgba(255,255,255,.25), transparent 60%)`,
              mixBlendMode: "soft-light",
              opacity: hovered ? 1 : 0,
              transition: "opacity 200ms ease",
              transform: "translateZ(40px)",
            }}
          />

          {/* Content */}
          <div
            className="relative z-10"
            style={{ transform: "translateZ(50px)" }}
          >
            {children}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
const CaseStudies = () => (
  <Section id="work" className="py-20">
    <div className="mb-10 text-center">
      <h2 className="text-3xl font-semibold md:text-4xl">
        Portfolio highlights
      </h2>
      <p className="mt-2 text-zinc-600 dark:text-zinc-300">
        Real spaces we’ve helped list and support.
      </p>
    </div>
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[
        {
          city: "Maple Street Driveway",
          impact: "Clear house rules, photo gallery",
          img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1974&auto=format&fit=crop",
          tag: "Residential",
        },
        {
          city: "Union Hall Overflow Lot",
          impact: "Weekend availability calendar",
          img: "https://images.unsplash.com/photo-1506521781263-d8422e82f27a?q=80&w=1974&auto=format&fit=crop",
          tag: "Community",
        },
        {
          city: "Pier 45 Covered Spots",
          impact: "Directions + height limits",
          img: "https://images.unsplash.com/photo-1493238792000-8113da705763?q=80&w=1974&auto=format&fit=crop",
          tag: "Garage",
        },
      ].map((c) => (
        <TiltCard key={c.city}>
          <div className="relative overflow-hidden rounded-xl">
            {/* Image */}
            <img
              src={c.img}
              alt={c.city}
              loading="lazy"
              className="h-56 w-full object-cover md:h-60"
              sizes="(min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw"
            />
            {/* Top info bar */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/55 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute left-3 top-3 inline-flex items-center gap-2 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-zinc-800 ring-1 ring-black/5 dark:bg-white/10 dark:text-white dark:ring-white/10">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />{" "}
              {c.tag}
            </div>
          </div>
          {/* Caption */}
          <div className="mt-4">
            <h3 className="text-lg font-semibold tracking-tight">{c.city}</h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              {c.impact}
            </p>
          </div>
        </TiltCard>
      ))}
    </div>
  </Section>
);
const Stats = () => {
  // Animate integers with existing useCounter hook
  const a = useCounter(120, 1.2); // cities or listings
  const b = useCounter(48, 1.2); // questions answered (K)
  const c = useCounter(99, 1.2); // satisfaction %

  const Stat = ({ icon: Icon, value, suffix = "", label, sub }) => (
    <motion.div
      whileHover={{ y: -2 }}
      className="relative rounded-2xl p-5 ring-1 ring-white/10 bg-white/70 dark:bg-zinc-900/60 shadow-sm"
    >
      {/* soft halo */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-2xl bg-[conic-gradient(from_220deg_at_50%_50%,theme(colors.emerald.400/15),theme(colors.cyan.400/15),theme(colors.indigo.500/15),theme(colors.emerald.400/15))] opacity-0 blur transition-opacity duration-300 group-hover:opacity-100"
      />
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/20">
          <Icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        </span>
        <div>
          <div className="flex items-baseline gap-1">
            <p className="text-4xl font-semibold tracking-tight">{value}</p>
            {suffix && (
              <span className="text-xl text-white/80 dark:text-white/70">
                {suffix}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {label}
          </p>
          {sub && <p className="mt-0.5 text-xs text-zinc-500">{sub}</p>}
        </div>
      </div>
    </motion.div>
  );

  return (
    <Section className="py-16">
      {/* wrapper with gradient border */}
      <div className="rounded-3xl p-[1px] bg-[conic-gradient(from_210deg_at_50%_50%,theme(colors.emerald.500/30),theme(colors.cyan.500/30),theme(colors.indigo.600/30),theme(colors.emerald.500/30))]">
        <div className="rounded-3xl bg-white/60 dark:bg-zinc-950/60 backdrop-blur-md p-6 md:p-8">
          <div className="mb-6 flex items-center justify-between gap-4">
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
              At a glance
            </h3>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-zinc-300/50 to-transparent dark:via-white/10" />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Stat
              icon={MapPin}
              value={a}
              suffix="+"
              label="active listings / cities"
              sub="Growing week over week"
            />
            <Stat
              icon={Phone}
              value={b}
              suffix="K"
              label="questions answered"
              sub="Email & chat support"
            />
            <Stat
              icon={Star}
              value={c}
              suffix="%"
              label="support satisfaction"
              sub="Past 12 months"
            />
          </div>
        </div>
      </div>
    </Section>
  );
};

const Testimonials = () => (
  <Section className="py-20">
    <div className="mb-10 text-center">
      <h2 className="text-3xl font-semibold md:text-4xl">What people say</h2>
      <p className="mt-2 text-zinc-600 dark:text-zinc-300">
        Listings that are easy to understand. Support that actually helps.
      </p>
    </div>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[
        {
          name: "Riley Brooks",
          role: "Homeowner",
          quote:
            "They walked me through photos and house rules. My listing went live fast and messages are way easier now.",
          color: "bg-emerald-500",
        },
        {
          name: "Jordan Kim",
          role: "Church Admin",
          quote:
            "Weekend overflow used to confuse visitors. The new listing and directions cleared it up in a day.",
          color: "bg-cyan-500",
        },
        {
          name: "Ava Patel",
          role: "Parker",
          quote:
            "Support replied in minutes and pointed me to a nearby driveway option. Super friendly and simple.",
          color: "bg-indigo-500",
        },
      ].map((t) => (
        <motion.blockquote
          key={t.name}
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 24 }}
          viewport={{ once: true }}
          className="group relative overflow-hidden rounded-2xl border border-zinc-200/60 bg-white/70 p-6 shadow-sm transition hover:shadow-xl dark:border-white/10 dark:bg-zinc-900/60"
        >
          {/* glow */}
          <span
            aria-hidden
            className="pointer-events-none absolute -inset-px -z-10 rounded-2xl bg-[conic-gradient(from_220deg_at_50%_50%,theme(colors.emerald.400/15),theme(colors.cyan.400/15),theme(colors.indigo.500/15),theme(colors.emerald.400/15))] opacity-0 blur transition-opacity duration-300 group-hover:opacity-100"
          />

          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span
                className={`grid h-10 w-10 place-items-center rounded-full text-white ${t.color}`}
              >
                {t.name
                  .split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <div>
                <div className="font-semibold text-zinc-900 dark:text-white">
                  {t.name}
                </div>
                <div className="text-xs text-zinc-500">{t.role}</div>
              </div>
            </div>
            <div
              className="flex items-center gap-1 text-amber-500"
              aria-label="5 star rating"
            >
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4" />
              ))}
            </div>
          </div>

          <p className="text-zinc-700 dark:text-zinc-200">“{t.quote}”</p>
        </motion.blockquote>
      ))}
    </div>
  </Section>
);

const FAQItem = ({ q, a, Icon }) => {
  return (
    <details className="group rounded-2xl border border-white/10 bg-white/70 p-4 dark:bg-zinc-900/60 transition hover:shadow-lg dark:border-white/10">
      <summary className="flex cursor-pointer list-none items-start gap-3">
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/20 text-emerald-600 dark:text-emerald-400">
          <Icon className="h-4 w-4" />
        </span>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
              {q}
            </h3>
            <ChevronRight className="h-5 w-5 text-zinc-500 transition-transform group-open:rotate-90" />
          </div>
        </div>
      </summary>

      <AnimatePresence initial={false}>
        {/* Animate the answer reveal */}
        <motion.div
          key="answer"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="overflow-hidden pl-11"
        >
          <p className="pt-3 text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
            {a}
          </p>
        </motion.div>
      </AnimatePresence>
    </details>
  );
};

const FAQ = () => (
  <Section id="faq" className="py-20">
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mx-auto mb-10 max-w-3xl text-center">
        <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/60 px-3 py-1 text-[11px] font-medium text-zinc-700 backdrop-blur dark:bg-white/5 dark:text-zinc-300">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
          Event parking, made clear
        </div>
        <h2 className="text-3xl font-semibold md:text-4xl">
          Frequently asked questions
        </h2>
        <p className="mt-2 text-zinc-600 dark:text-zinc-300">
          Everything you need to know about smart ingress, passes, and payments.
        </p>
      </div>

      {/* Grid */}
      <div
        className="
          grid gap-4
          md:grid-cols-2
        "
      >
        {faqs.map(({ q, a, icon: Icon }, i) => (
          <FAQItem key={i} q={q} a={a} Icon={Icon} />
        ))}
      </div>

      {/* Footer note */}
      <div className="mt-8 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4 text-sm text-emerald-700 dark:text-emerald-300">
        Can’t find an answer?{" "}
        <a
          href="#contact"
          className="font-semibold underline decoration-emerald-400/60 underline-offset-4 hover:decoration-emerald-400"
        >
          Talk to an engineer
        </a>{" "}
        and we’ll tailor a plan for your venue or city.
      </div>
    </div>
  </Section>
);

const CTA = () => (
  <Section id="contact" className="py-20">
    <div className="relative overflow-hidden rounded-3xl p-[1px]">
      {/* conic border frame */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 rounded-3xl blur"
        style={{
          background:
            "conic-gradient(from 180deg at 50% 50%, rgba(16,185,129,.35), rgba(6,182,212,.35), rgba(79,70,229,.35), rgba(16,185,129,.35))",
        }}
      />
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-black p-8 text-white md:p-12">
        <AnimatedBlobs />
        <div className="relative mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-extrabold tracking-tight md:text-4xl">
            Have a spot to list or a parking question?
          </h2>
          <p className="mt-3 text-white/70">
            We help with listings, customer support, and parking ticket
            guidance.
          </p>
          <div className="mt-7 flex items-center justify-center">
            <Button
              href="#contact-panel"
              className="bg-white text-zinc-900 hover:bg-white/90"
              icon={Mail}
              size="lg"
            >
              Contact Us
            </Button>
          </div>

          {/* subtle badges */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs text-white/60">
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
              Typical reply{" "}
              <span className="font-semibold text-white">under 24h</span>
            </span>
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
              For: Hosts • Drivers • Venues • Cities
            </span>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

const Footer = () => (
  <footer className="mt-20 text-sm text-zinc-500">
    {/* subtle gradient top border */}
    <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-zinc-200/80 to-transparent dark:via-white/10" />

    <Section className="py-10">
      <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
        {/* Brand + copyright */}
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-emerald-500 via-cyan-500 to-indigo-600 text-white shadow-sm">
            <ParkingMeter className="h-4 w-4" />
          </div>
          <div>
            <div className="font-medium text-zinc-900 dark:text-zinc-100">
              Park Dat
            </div>
            <div className="text-xs">
              © {new Date().getFullYear()} Park Dat. All rights reserved.
            </div>
          </div>
        </div>

        {/* Contact strip */}
        <div className="flex w-full flex-col gap-4 md:w-auto md:flex-row md:items-center md:gap-6">
          <a
            href="tel:+18885025242"
            className="
      group inline-flex items-center gap-2 rounded-xl
      border border-emerald-400/60
      bg-white/30 px-3 py-2 text-zinc-800 font-medium
      shadow-sm transition
      hover:bg-white/60 hover:text-black hover:border-emerald-400
      focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50
      supports-[backdrop-filter]:backdrop-blur-md
      dark:border-emerald-400/40 dark:bg-zinc-800/40 dark:text-zinc-300
      dark:hover:text-white dark:hover:bg-zinc-800/60
    "
          >
            <Phone className="h-4 w-4 text-emerald-600 transition group-hover:scale-110 group-hover:text-black dark:text-emerald-500 dark:group-hover:text-white" />
            <span>+1 (888) 502-5242</span>
          </a>

          <a
            href="https://maps.google.com/?q=3447%20Renner%20Rd%20Ste%20120%2C%20Plano%2C%20TX%2075074"
            target="_blank"
            rel="noreferrer"
            className="
    group inline-flex items-center gap-2 rounded-xl
    border border-emerald-400/60
    bg-white/30 px-3 py-2 text-zinc-800 font-medium
    shadow-sm transition
    hover:bg-white/60 hover:text-black hover:border-emerald-400
    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50
    supports-[backdrop-filter]:backdrop-blur-md

    dark:border-emerald-400/40 dark:bg-zinc-800/40 dark:text-zinc-300
    dark:hover:text-white dark:hover:bg-zinc-800/60
  "
          >
            <MapPin className="h-4 w-4 text-emerald-600 transition group-hover:scale-110 group-hover:text-black dark:text-emerald-500 dark:group-hover:text-white" />
            <span>3447 Renner Rd, Ste 120, Plano, TX 75074</span>
          </a>

          <a
            href="mailto:sales@parkdat.com"
            className="
    group inline-flex items-center gap-2 rounded-xl
    border border-emerald-400/60
    bg-white/30 px-3 py-2 text-zinc-800 font-medium
    shadow-sm transition
    hover:bg-white/60 hover:text-black hover:border-emerald-400
    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50
    supports-[backdrop-filter]:backdrop-blur-md

    dark:border-emerald-400/40 dark:bg-zinc-800/40 dark:text-zinc-300
    dark:hover:text-white dark:hover:bg-zinc-800/60
  "
          >
            <Mail className="h-4 w-4 text-emerald-600 transition group-hover:scale-110 group-hover:text-black dark:text-emerald-500 dark:group-hover:text-white" />
            <span>sales@parkdat.com</span>
          </a>
        </div>
      </div>

      {/* tiny legal line */}
      <div className="mt-6 flex flex-col items-start justify-between gap-3 text-xs text-zinc-400 md:flex-row md:items-center">
        <div>
          Serving listings, customer support, and parking ticket guidance.
        </div>
        <div className="flex gap-4">
          <a
            href="/privacy"
            className="hover:text-black dark:hover:text-white transition"
          >
            Privacy Policy
          </a>
          <span aria-hidden>•</span>
          {/* <a
            href="#"
            className="hover:text-black dark:hover:text-white transition"
          >
            Terms
          </a> */}
        </div>
      </div>
    </Section>
  </footer>
);

const FabProgress = () => {
  const prefersReduced = useReducedMotion();
  const { scrollYProgress } = useScroll();

  // Geometry
  const size = 56; // outer SVG size
  const radius = 22; // ring radius
  const stroke = 4;
  const circumference = 2 * Math.PI * radius;

  // Progress (0 -> circumference)
  const dash = useTransform(scrollYProgress, [0, 1], [0, circumference]);
  const dashOffset = useTransform(dash, (d) => circumference - d);

  // Show/hide & subtle lift after we scroll a bit
  const visible = useTransform(scrollYProgress, [0.08, 0.12], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [8, 0]);

  return (
    <motion.button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      initial={false}
      style={{ opacity: visible, y }}
      whileHover={prefersReduced ? {} : { scale: 1.04 }}
      whileTap={prefersReduced ? {} : { scale: 0.98 }}
      className="
        group fixed bottom-6 right-6 z-50 grid h-14 w-14 place-items-center
        rounded-2xl bg-white/85 dark:bg-zinc-900/80 backdrop-blur-md
        shadow-[0_12px_40px_-10px_rgba(0,0,0,.35)]
        ring-1 ring-zinc-200/80 dark:ring-white/10
        transition-all
        focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400
      "
    >
      {/* Soft glow */}
      <span
        aria-hidden
        className="
          pointer-events-none absolute inset-0 -z-10 rounded-2xl
          bg-[radial-gradient(60%_60%_at_50%_50%,theme(colors.emerald.400/18),transparent)]
          opacity-0 group-hover:opacity-100 transition-opacity
        "
      />

      {/* Conic progress halo behind the button */}
      {!prefersReduced && (
        <motion.span
          aria-hidden
          className="
            absolute -inset-1 rounded-2xl
            bg-[conic-gradient(var(--tw-gradient-stops))]
            from-emerald-400/0 via-cyan-400/25 to-indigo-500/0
            blur
          "
          style={{ opacity: visible }}
        />
      )}

      {/* Progress ring */}
      <motion.svg
        width={size}
        height={size}
        viewBox="0 0 56 56"
        className="absolute"
        aria-hidden
      >
        {/* Base ring */}
        <circle
          cx="28"
          cy="28"
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-zinc-300/60 dark:text-white/15"
          strokeWidth={stroke}
        />
        {/* Animated progress ring */}
        <motion.circle
          cx="28"
          cy="28"
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-emerald-500"
          strokeLinecap="round"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          style={{ strokeDashoffset: dashOffset }}
        />
      </motion.svg>

      {/* Inner pill + icon */}
      <span
        className="
          relative grid h-10 w-10 place-items-center rounded-xl
          bg-white/80 dark:bg-zinc-900/80
          ring-1 ring-inset ring-zinc-200/70 dark:ring-white/10
          shadow-inner
        "
      >
        <ArrowUp
          className="
            h-5 w-5 text-zinc-800 dark:text-zinc-100
            transition-transform duration-300
            group-hover:-translate-y-0.5
          "
        />
      </span>
    </motion.button>
  );
};

export default function ExToParkLanding() {
  return (
    <div className="min-h-screen scroll-smooth bg-white text-zinc-900 antialiased dark:bg-zinc-950 dark:text-white">
      <Spotlight />
      <Navbar />
      <Hero />
      <Clients />
      <Services />
      <Events />
      <CaseStudies />
      <Stats />
      <Testimonials />
      <FAQ />
      <CTA />
      <Footer />
      <FabProgress />
      <ContactOverlay />
    </div>
  );
}

// Tailwind helpers (add this to your global CSS if missing):
// .animate-spin-slow { animation: spin 18s linear infinite; }
